import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import { styles } from "./style";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { AddRoute } from "./add-route";
import Paper from "@mui/material/Paper";
import { getCampaignRoutes } from "redux/selector";
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { urls } from "routes/urls";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { CampaignRoute } from "model/campaign-route";
import { deleteCampaignRoute, updateCampaignRoute } from "redux/slice/campaign-routes";
import { TriggerType } from "type/trigger-type";
import { LeadSourceType } from "type/lead-source-type";
import { EnrollmentModeType } from "type/enrollment-mode-type";
import { CampaignStatus } from "type/campaign-status";
import { Dialog } from "component/shared/dialog";
import { baseColors } from "theme";

interface OwnProps {}

interface Props extends
	OwnProps,
	PageProps,
	Router.RouteComponentProps,
	WithStyles<typeof styles>,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps> {
}

interface State {
	openDialog: boolean;
	campaignRoute?: CampaignRoute;
	edit?: boolean;
	routeToDelete?: CampaignRoute;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
		campaignRoutes: getCampaignRoutes(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	deleteCampaignRoute,
	updateCampaignRoute,
}, dispatch);

function getTriggerLabel(id: string): string {
	return TriggerType.getById(id)?.label
		|| LeadSourceType.getById(id)?.label
		|| id;
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			openDialog: false,
		};
	}

	public render() {
		const { user, classes = {} } = this.props;
		const { openDialog, campaignRoute, edit, routeToDelete } = this.state;
		const title = "Automations";
		const routes = this.props.campaignRoutes;

		return (
			<DashboardLayout
				permitted={user && user.permissions.campaigns}
				title={title}
				header={
					<Mui.Typography variant="h1">
						<FeatherIcon>
							<Icons.Send />
						</FeatherIcon>
						{title}
					</Mui.Typography>
				}
			>
				{/* Add / Edit automation modal */}
				<AddRoute
					campaignRoute={campaignRoute}
					open={openDialog}
					edit={edit}
					onClose={() => this.setState({ openDialog: false, campaignRoute: undefined })}
				/>
				{/* Delete confirmation modal */}
				<Dialog
					open={!!routeToDelete}
					onClose={() => this.setState({ routeToDelete: undefined })}
					maxWidth="sm"
					fullWidth
				>
					<Mui.DialogTitle>Delete automation?</Mui.DialogTitle>
					<Mui.DialogContent>
						<Mui.Typography>
							Leads will no longer be automatically enrolled based on these triggers. This cannot be undone.
						</Mui.Typography>
					</Mui.DialogContent>
					<Mui.DialogActions style={{ padding: '16px 24px' }}>
						<Mui.Button variant="outlined" color="primary" onClick={() => this.setState({ routeToDelete: undefined })}>
							Cancel
						</Mui.Button>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={() => {
								if (routeToDelete) {
									this.props.deleteCampaignRoute({ campaignRoute: routeToDelete });
								}
								this.setState({ routeToDelete: undefined });
							}}
						>
							Delete Automation
						</Mui.Button>
					</Mui.DialogActions>
				</Dialog>
				<Mui.Grid container direction="column" spacing={2}>

					{/* Page description */}
					<Mui.Grid item>
						<Mui.Typography variant="body2" color="textSecondary">
							Manage your campaign automations. Set activity-based triggers to automatically enroll leads into campaigns the moment they take action.
						</Mui.Typography>
					</Mui.Grid>

					{/* Add button */}
					<Mui.Grid item style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={() => this.setState({ openDialog: true, edit: false })}
						>
							+ Add Automation
						</Mui.Button>
					</Mui.Grid>

					{/* Rules table */}
					<Mui.Grid item>
						<Mui.TableContainer component={Paper} style={{ boxShadow: 'none' }}>
							<Mui.Table aria-label="Automation Rules">
								<Mui.TableHead>
									<Mui.TableRow>
										<Mui.TableCell className={classes.tableHeaderCell}>Automation Triggers</Mui.TableCell>
										<Mui.TableCell className={classes.tableHeaderCell} style={{ minWidth: 170 }}>Campaign</Mui.TableCell>
										<Mui.TableCell className={classes.tableHeaderCell} style={{ minWidth: 210 }}>If already completed</Mui.TableCell>
										{/* MAX-3873: read-only lifetime enrollment count. Right-aligned to read as a numeric column (per Figma). */}
										<Mui.TableCell className={classes.tableHeaderCell} align="right">Enrollments</Mui.TableCell>
										<Mui.TableCell className={classes.tableHeaderCell} align="center">Active</Mui.TableCell>
										<Mui.TableCell style={{ width: 90 }} />
									</Mui.TableRow>
								</Mui.TableHead>
								<Mui.TableBody>
									{routes.length === 0 && (
										<Mui.TableRow>
											<Mui.TableCell colSpan={6}>
												<Mui.Typography variant="body2" color="textSecondary" align="center">
													No automation rules configured yet. Add a rule to get started.
												</Mui.Typography>
											</Mui.TableCell>
										</Mui.TableRow>
									)}
									{routes.map((route) => (
										<Mui.TableRow key={route.id}>
											<Mui.TableCell>
												<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
													{route.triggers.map((id) => (
														<Mui.Chip
															key={id}
															label={getTriggerLabel(id)}
															style={{
																background: baseColors.gray75,
																color: baseColors.teal600,
																padding: '4px 8px',
															}}
														/>
													))}
												</div>
											</Mui.TableCell>
											<Mui.TableCell>
												{!route.campaign || route.campaign.inactiveYn ? (
													// MAX-3842: deleted (route.campaign null, legacy rows) or soft-deleted
													// (route.campaign.inactiveYn -- what Delete Campaign actually sets) --
													// per Figma, this fully replaces the campaign name. Distinct from merely
													// pausing a campaign via its Active toggle (status, below), which does
													// not delete anything and keeps this automation activatable.
													// Unwrapped -- FeatherIcon renders via MUI's SvgIcon, which ignores the
													// size prop passed to the inner icon.
													(<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
														<Icons.AlertTriangle size={16} color={baseColors.orange} style={{ flexShrink: 0 }} />
														<Mui.Typography variant="body2">No Campaign</Mui.Typography>
													</div>)
												) : (
													<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
														<RouterLinkWrapper className={classes.link} style={{ color: 'inherit', textDecoration: 'underline' }} to={urls.campaign(route.campaign)}>
															{route.campaign.name}
														</RouterLinkWrapper>
														{route.campaign.status === CampaignStatus.INACTIVE && (
															// MAX-3874: the linked campaign still exists and hasn't been deleted, just
															// paused -- warn the user, but (unlike "No Campaign") this doesn't block
															// activation below.
															(<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
																<Icons.AlertTriangle size={16} color={baseColors.orange} style={{ flexShrink: 0 }} />
																<Mui.Typography variant="body2">Inactive Campaign</Mui.Typography>
															</div>)
														)}
													</div>
												)}
											</Mui.TableCell>
											<Mui.TableCell>
												<Mui.Typography variant="body2">
													{EnrollmentModeType.getById(route.enrollmentMode)?.label || route.enrollmentMode}
													{route.enrollmentMode === EnrollmentModeType.RE_ENGAGE.id && route.reEngageDays != null && (
														<Mui.Typography component="span" variant="caption" color="textSecondary">
															{' '}({route.reEngageDays} days)
														</Mui.Typography>
													)}
												</Mui.Typography>
											</Mui.TableCell>
											<Mui.TableCell align="right">
												{/* MAX-3873: bare integer, renders 0 (never blank/dash). Value always present on the
													read path via the resource spread; ?? 0 satisfies the optional model type. */}
												<Mui.Typography variant="body2">
													{route.enrollmentCount ?? 0}
												</Mui.Typography>
											</Mui.TableCell>
											<Mui.TableCell align="center">
												{(() => {
													// MAX-3842: no campaign, or the linked campaign has been deleted -- can't activate
													// either way. A merely-paused (status INACTIVE, not deleted) campaign does NOT
													// block activation -- matches the pre-existing MAX-3874 behavior above.
													const hasCampaign = !!route.campaign && !route.campaign.inactiveYn;
													const switchEl = (
														<Mui.Switch
															// MAX-3857: leave `inactive` untouched so the route resumes its prior state if reassigned.
															checked={!route.inactive && hasCampaign}
															disabled={!hasCampaign}
															// MAX-3857: guard here too, not just via `disabled` -- updateCampaignRoute requires a campaign.
															onChange={() => hasCampaign && this.props.updateCampaignRoute({ campaignRoute: { ...route, inactive: !route.inactive } })}
															color="secondary"
															size="small"
														/>
													);
													return hasCampaign ? switchEl : (
														<Mui.Tooltip title="This automation can't be activated until a campaign is assigned.">
															<span>{switchEl}</span>
														</Mui.Tooltip>
													);
												})()}
											</Mui.TableCell>
											<Mui.TableCell align="right" style={{ whiteSpace: 'nowrap', width: 90 }}>
												<Mui.IconButton
													size="small"
													style={{ color: baseColors.teal }}
													onClick={() => this.setState({ campaignRoute: route, openDialog: true, edit: true })}
												>
													<FeatherIcon><Icons.Edit /></FeatherIcon>
												</Mui.IconButton>
												<Mui.IconButton
													size="small"
													style={{ color: baseColors.brightRed }}
													onClick={() => this.setState({ routeToDelete: route })}
												>
													<FeatherIcon><Icons.Trash2 /></FeatherIcon>
												</Mui.IconButton>
											</Mui.TableCell>
										</Mui.TableRow>
									))}
								</Mui.TableBody>
							</Mui.Table>
						</Mui.TableContainer>
					</Mui.Grid>

				</Mui.Grid>
			</DashboardLayout>
		);
	}
}

export const CampaignRoutingPage = withStyles(connect(
	mapStateToProps,
	mapDispatchToProps,
)(Component), styles);
